<script setup lang="ts">
/**
 * A recipient field of a mail (#1119): To, Cc or Bcc, holding as many addresses as the writer adds, each a chip. An
 * address is typed into the field as before, with the mailbox's contacts offered as it is typed (`MailRecipientInput`),
 * and becomes a chip when a suggestion is picked, or on Enter, a comma or a semicolon, or when the field is left.
 * Backspace in the empty field takes the last chip off, and each chip has its own remove button. The model is every
 * chip and whatever is still being typed, so a mail sent with an address half-entered still goes to it.
 */
import { X } from '@lucide/vue'
import { ref, watch } from 'vue'
import type { MailContact } from '@/api'
import MailRecipientInput from '@/components/MailRecipientInput.vue'

const props = withDefaults(
    defineProps<{
        modelValue: string[]
        /** The mailbox's contacts matching what was typed. Absent, nothing is offered. */
        search?: ((text: string) => Promise<MailContact[]>) | undefined
        disabled?: boolean
        /** Whether the field must name somebody: its input is required only while it holds no chip. */
        required?: boolean
        /** The `data-testid` the input carries, as the single field it replaces did (`mail-compose-to`). */
        testid?: string
        /** What the field is, for the screen reader: To, Cc or Bcc. */
        label?: string
    }>(),
    { search: undefined, disabled: false, required: false, testid: undefined, label: 'To' },
)
const emit = defineEmits<{ 'update:modelValue': [string[]] }>()

/** What separates two addresses typed or pasted into one field. */
const SEPARATOR = /[,;]/

const chips = ref<string[]>([...props.modelValue])
const text = ref('')
/** What the field last told its parent, so its own update coming back is not read as a reset. */
let told: string[] = [...props.modelValue]

function tell(): void {
    const typed = text.value.trim()
    told = typed === '' ? [...chips.value] : [...chips.value, typed]
    emit('update:modelValue', told)
}

// A parent that sets the field (a reply-all's Cc, a form emptied after a send) replaces what is in it.
watch(
    () => props.modelValue,
    value => {
        if (value.length === told.length && value.every((address, index) => address === told[index])) {
            return
        }
        chips.value = [...value]
        text.value = ''
        told = [...value]
    },
)

/** Each address in `typed` as a chip, once: one already there is not added again, whatever its case. */
function add(typed: string): void {
    for (const part of typed.split(SEPARATOR)) {
        const address = part.trim()
        if (address !== '' && !chips.value.some(chip => chip.toLowerCase() === address.toLowerCase())) {
            chips.value = [...chips.value, address]
        }
    }
}

/** What is typed, made a chip. */
function commit(): void {
    if (text.value.trim() === '') {
        return
    }
    add(text.value)
    text.value = ''
    tell()
}

function onInput(value: string): void {
    // A comma or semicolon typed or pasted ends an address: everything before the last one becomes chips.
    if (SEPARATOR.test(value)) {
        const parts = value.split(SEPARATOR)
        add(parts.slice(0, -1).join(','))
        text.value = (parts.at(-1) ?? '').trimStart()
    } else {
        text.value = value
    }
    tell()
}

function onChosen(address: string): void {
    text.value = ''
    add(address)
    tell()
}

function remove(index: number): void {
    chips.value = chips.value.filter((_, at) => at !== index)
    tell()
}

/** Enter makes what is typed a chip; with nothing typed, or a suggestion just picked, it is left to the form. */
function onEnter(event: KeyboardEvent): void {
    if (event.defaultPrevented || text.value.trim() === '') {
        return
    }
    event.preventDefault()
    commit()
}

function onBackspace(): void {
    if (text.value === '' && chips.value.length > 0) {
        remove(chips.value.length - 1)
    }
}
</script>

<template>
	<div
		class="flex flex-wrap items-center gap-1"
		:data-testid="testid ? `${testid}-field` : undefined"
		@keydown.enter="onEnter"
		@keydown.backspace="onBackspace"
		@focusout="commit"
	>
		<span
			v-for="(chip, index) in chips"
			:key="chip"
			class="bg-muted inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs"
			data-testid="mail-recipient-chip"
			:data-address="chip"
		>
			{{ chip }}
			<button
				type="button"
				class="text-muted-foreground hover:text-foreground"
				:aria-label="`Remove ${chip} from ${label}`"
				:disabled="disabled"
				data-testid="mail-recipient-chip-remove"
				@click="remove(index)"
			>
				<X class="size-3" aria-hidden="true" />
			</button>
		</span>
		<MailRecipientInput
			class="min-w-40 flex-1"
			:model-value="text"
			:search="search"
			:disabled="disabled"
			:required="required && chips.length === 0"
			:testid="testid"
			@update:model-value="onInput"
			@chosen="onChosen"
		/>
	</div>
</template>
